import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useTenant } from '../../lib/tenant-context';
import { useLoyaltyDashboard, useLoyaltyToggle } from '../../lib/loyalty-hooks';
import { Card } from '../../components/ui/Card';
import { Users, Star, Gift, Trophy, Power, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
export function LoyaltyDashboardPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const {
    customers,
    loading
  } = useLoyaltyDashboard(restaurant.restaurant_id);
  const {
    isActive,
    loading: toggleLoading,
    saving,
    toggle
  } = useLoyaltyToggle(restaurant.restaurant_id);
  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + (c.current_points || 0), 0);
  const totalRewardsAvailable = customers.reduce((sum, c) => sum + (c.available_rewards_count || 0), 0);
  const totalRewardsRedeemed = customers.reduce((sum, c) => sum + (c.redeemed_rewards_count || 0), 0);
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-4925">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-4926">
        <div data-id="element-4927">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-4928">
            Programa de Lealtad
          </h1>
          <p className="text-gray-500" data-id="element-4929">
            Gestiona tus clientes frecuentes y beneficios
          </p>
        </div>

        {/* Activation toggle */}
        <Card className={`p-6 border-2 transition-colors ${isActive ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50'}`} data-id="element-4930">
          <div className="flex items-center justify-between gap-6 flex-wrap" data-id="element-4931">
            <div className="flex items-center gap-4" data-id="element-4932">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-200'}`} data-id="element-4933">
                <Power className={`w-6 h-6 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-4934" />
              </div>
              <div data-id="element-4935">
                <div className="flex items-center gap-2" data-id="element-4936">
                  <h2 className="font-bold text-gray-900" data-id="element-4937">
                    Sistema de Lealtad
                  </h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-4938">
                    {toggleLoading ? '...' : isActive ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-4939">
                  {isActive ? 'Cada pedido pagado con teléfono acumula puntos para el cliente.' : 'Los pedidos no acumulan puntos. Activa el programa para empezar a premiar a tus clientes.'}
                </p>
              </div>
            </div>

            <button onClick={() => toggle(!isActive)} disabled={saving || toggleLoading} className={`relative inline-flex items-center h-9 px-1 rounded-full transition-colors w-20 shrink-0 disabled:opacity-60 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-label={isActive ? 'Desactivar lealtad' : 'Activar lealtad'} data-id="element-4940">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow transform transition-transform ${isActive ? 'translate-x-11' : 'translate-x-0'}`} data-id="element-4941">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" data-id="element-4942" />}
              </span>
            </button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4943">
          <Card className="p-6" data-id="element-4944">
            <div className="flex items-center gap-4" data-id="element-4945">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4946">
                <Users className="w-6 h-6 text-blue-600" data-id="element-4947" />
              </div>
              <div data-id="element-4948">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4949">
                  Clientes Leales
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4950">
                  {totalCustomers}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-4951">
            <div className="flex items-center gap-4" data-id="element-4952">
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4953">
                <Star className="w-6 h-6 text-yellow-600" data-id="element-4954" />
              </div>
              <div data-id="element-4955">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4956">
                  Puntos Emitidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4957">
                  {totalPoints}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-4958">
            <div className="flex items-center gap-4" data-id="element-4959">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4960">
                <Gift className="w-6 h-6 text-green-600" data-id="element-4961" />
              </div>
              <div data-id="element-4962">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4963">
                  Beneficios Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4964">
                  {totalRewardsAvailable}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-4965">
            <div className="flex items-center gap-4" data-id="element-4966">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4967">
                <Trophy className="w-6 h-6 text-purple-600" data-id="element-4968" />
              </div>
              <div data-id="element-4969">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4970">
                  Beneficios Redimidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4971">
                  {totalRewardsRedeemed}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="overflow-hidden" data-id="element-4972">
          <div className="p-6 border-b border-gray-200" data-id="element-4973">
            <h2 className="font-bold text-gray-900" data-id="element-4974">Directorio de Clientes</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-4975">
            <table className="w-full text-left text-sm" data-id="element-4976">
              <thead className="bg-gray-50 text-gray-500" data-id="element-4977">
                <tr data-id="element-4978">
                  <th className="px-6 py-4 font-medium" data-id="element-4979">Cliente</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4980">Teléfono</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-4981">Visitas</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-4982">
                    Total Gastado
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-4983">
                    Ticket Promedio
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-4984">Puntos</th>
                  <th className="px-6 py-4 font-medium" data-id="element-4985">Nivel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-4986">
                {loading ? <tr data-id="element-4987">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-4988">
                      Cargando clientes...
                    </td>
                  </tr> : customers.length === 0 ? <tr data-id="element-4989">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-4990">
                      No hay clientes registrados aún.
                    </td>
                  </tr> : customers.map(customer => <tr key={customer.customer_id} className="hover:bg-gray-50" data-id="element-4991">
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-4992">
                        {customer.name || 'Sin nombre'}
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-4993">
                        {customer.phone}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-4994">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-4995">
                        {formatCurrency(customer.total_spent, restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500" data-id="element-4996">
                        {formatCurrency(customer.total_spent / Math.max(1, customer.total_orders), restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-brand-charcoal" data-id="element-4997">
                        {customer.current_points}
                      </td>
                      <td className="px-6 py-4" data-id="element-4998">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4999">
                          {customer.current_level || 'Inicial'}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>;
}